import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { giveConsent } from "@/redux/actions/testKitActions";
import { useState } from "react";
import { useDispatch } from "react-redux";

const ConsentForm: React.FC<{
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  kitId: string;
  testId: number;
}> = ({ isOpen, setIsOpen, kitId, testId }) => {
  const [isChecked, setIsChecked] = useState(false);

  const dispatch: any = useDispatch();

  const { toast } = useToast();

  const handleCheckedChange = (checked: boolean) => {
    setIsChecked(checked);
  };

  const handleSubmit = () => {
    dispatch(
      giveConsent(testId, (message) => {
        setIsOpen(false);
        toast({
          description: message,
        });
      })
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-[800px] sm:max-w-full overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Patient Informed Consent Form</SheetTitle>
          <SheetDescription>KIT ID #{kitId}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 text-sm text-gray-500">
          <div className="mt-4 text-justify">
            Your{" "}
            <span className="text-black font-semibold">Informed Consent</span>{" "}
            is a vital step in ensuring transparency, respect for your autonomy,
            and ethical healthcare. It signifies your voluntary agreement to
            participate in the recommended gut microbiome testing process. By
            providing informed consent, you actively engage in the
            decision-making process, understanding the purpose, risks, and
            benefits of the test, as well as the handling of your data. This
            process establishes trust and communication between you, your
            treating physician, and FRX, fostering a collaborative approach to
            your healthcare.
          </div>

          <div className="mt-4 text-justify">
            This test involves collecting a stool sample to analyze the diverse
            community of microorganisms in your gut. Your treating physician,
            recognizing the significance of your health, has recommended this
            test to gain insights into your gut microbiome. This recommendation
            aligns with their commitment to your well-being, and they may
            integrate the results with their professional judgment on whether
            additional diagnostic tests are necessary. It is crucial to note
            that this test is not a standalone diagnostic tool and should
            complement, not replace, conventional diagnostic methods. Your
            treating physician will play a pivotal role in both the direct and
            indirect interpretation of the results, considering your existing
            health conditions, medications, and other relevant factors.
          </div>

          <div className="mt-4 text-justify">
            As the field of gut microbiome research is actively evolving, your
            treating physician will provide you with comprehensive insights and
            navigate the dynamic nature of interpreting results in the context
            of your unique medical profile. We appreciate your collaboration in
            this research, and we encourage you to thoroughly review this
            consent form to understand the intricacies of the gut microbiome
            test.
          </div>

          <div className="mt-4 text-right mr-6">Thank you,</div>
          <div className="text-right">The FRX Team</div>

          <Separator className="my-4" />

          <div>
            In consideration of undergoing the gut microbiome test as
            recommended by my treating physician, I hereby acknowledge and
            consent to the following:
          </div>

          <div className="mt-4 text-justify">
            <ol className="list-decimal list-inside">
              <li>
                I understand that I am taking the microbiome test based on the
                recommendation of my treating physician, who believes that the
                test may provide valuable information related to my health
                condition and treatment.
              </li>
              <li>
                I am aware that the microbiome test is provided by FRX, and I
                understand that the collected stool sample will be sent out of
                the country for processing and analysis by a third-party company
                selected by FRX.
              </li>
              <li>
                I acknowledge that my treating physician is the sole recipient
                of the microbiome test results, and it is entirely at their
                discretion to provide me with the results. I understand that the
                decision to share the results with me is based on medical
                considerations and the physician's professional judgment.
              </li>
              <li>
                I understand that my treating physician has consented to taking
                full responsibility for FRX's Terms of Service and Privacy
                Policy on behalf of him/herself and their patients (me),
                including full responsibility for the subsequent medical and
                ethical interpretation of the test results provided to me. I
                further acknowledge that FRX shall not bear any liability for
                the medical or ethical interpretation of the test results.
              </li>
              <li>
                I am aware that all data collected from me by FRX, including
                biological data such as test results (excluding personal data),
                may be used for research and commercial purposes at the
                company's discretion. I understand that my data will be
                de-identified to the extent possible to protect my privacy.
              </li>
            </ol>
          </div>

          <div className="mt-4 text-justify">
            Your informed consent is entirely voluntary, and you have the right
            to withdraw your consent at any time without any adverse
            consequences. If you choose to withdraw your consent, it will not
            affect your current or future healthcare with your treating
            physician. However, please be aware that withdrawing consent may
            limit your access to the specific gut microbiome testing services
            provided by FRX. If you decide to withdraw consent, kindly
            communicate your decision to your treating physician and FRX
            promptly. Your withdrawal will be respected and acknowledged,
            ensuring that your healthcare decisions align with your preferences.
          </div>

          <div className="mt-4 text-justify">
            By signing this consent form, I affirm that I have been provided
            with sufficient information to make an informed decision about
            undergoing the gut microbiome test and that I consent to the
            conditions outlined above.
          </div>

          <div className="flex items-center space-x-2 mt-4">
            <Checkbox id="terms" onCheckedChange={handleCheckedChange} />
            <label
              htmlFor="terms"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Accept terms and conditions
            </label>
          </div>
        </div>
        <SheetFooter>
          <Button
            disabled={!isChecked}
            type="submit"
            onClick={() => handleSubmit()}
          >
            Submit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default ConsentForm;
